import React from "react";
import { RetryContext } from "./RetryContext";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

interface Props {
  children: React.ReactNode;
}

export const RetryProvider = (props: Props) => {
  const [retryMessage, setRetryMessage] = React.useState<string | null>(null);
  const [cancellable, setCancellable] = React.useState<boolean>(false);
  const history = useHistory();

  const callbacks = React.useRef<
    { callback: () => void; cancellable: boolean }[]
  >([]);

  const { t } = useTranslation();

  const showRetry = React.useCallback(
    (callback: () => void, message?: string) => {
      callbacks.current.push({ callback, cancellable: false });
      setCancellable(false);
      setRetryMessage((previousMessage) => {
        return previousMessage ?? message ?? t("retry_default_message");
      });
    },
    [t]
  );

  const showRetryCancellable = React.useCallback(
    (callback: () => void, message?: string) => {
      callbacks.current.push({ callback, cancellable: true });
      setCancellable(callbacks.current.every(({ cancellable }) => cancellable));
      setRetryMessage((previousMessage) => {
        return previousMessage ?? message ?? t("retry_default_message");
      });
    },
    [t]
  );

  const onHide = React.useCallback(() => {
    setRetryMessage(null);
    const oldCallbacks = callbacks.current;
    callbacks.current = [];
    oldCallbacks.forEach(({ callback }) => {
      callback();
    });
  }, []);

  const onCancel = React.useCallback(() => {
    setRetryMessage(null);
    callbacks.current = [];
    history.push("/");
  }, [history]);

  const contextValue = React.useMemo(
    () => ({ showRetry, showRetryCancellable }),
    [showRetry, showRetryCancellable]
  );

  return (
    <>
      <Modal
        show={retryMessage != null}
        onHide={onHide}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>{retryMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            {t("retry_primary_button")}
          </Button>
          {cancellable && (
            <Button variant="secondary" onClick={onCancel}>
              {t("retry_cancel_button")}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <RetryContext.Provider value={contextValue}>
        {props.children}
      </RetryContext.Provider>
    </>
  );
};
